import GuideHint from "../GetStartedGuide/GuideHint";
import { formatCryptoValue, formatFiatValue, getOrderSummary } from "../../utils/format";
import { CreateCryptoOrder } from "../../../redux/actions/CryptoTransaction/Liquidity";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "../../../components/Common/Skeleton";
import NewCommonButton from "../common/NewCommonButton";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import { useCryptoRFQ } from "../../hooks/useCryptoRFQ";
import QuoteExpiryTimer from "../SwapModal/QuoteExpiryTimer";
import { cryptoPolicyURL, cryptoTremsCondiations } from "../../../constants";
import { ChevronRightIcon } from "../../icons";

const SummaryRow = ({
  label,
  value,
  valueClassName = "sm:text-base text-xs font-medium text-text-primary",
}) => (
  <div className="flex w-full items-center justify-between">
    <p className="sm:text-sm text-xs font-normal leading-[21px] text-text-secondary">
      {label}
    </p>
    <p className={`text-right ${valueClassName}`}>{value}</p>
  </div>
);

const getAmountTextClass = (value) => {
  const len = String(value).length;
  if (len <= 10) return "text-2xl max-sm:text-[36px]";
  if (len <= 14) return "text-xl max-sm:text-[30px]";
  if (len <= 18) return "text-lg max-sm:text-[26px]";
  return "text-base max-sm:text-[22px]";
};

const UnifiedConversionSummary = ({
  activeTab = "Buy",
  cryptoAccount,
  fiatAccount,
  amount,
  inputMode = "fiat",
  rfq,
  loading,
}) => {
  const isSell = activeTab === "Sell";
  const crypto_currency = cryptoAccount?.currency_code;
  const fiat_currency = fiatAccount?.currency_code;
  const unit_price = rfq?.data?.price;
  const fees = rfq?.fee ?? null;

  // Amount parsing
  let crypto_amount = 0;
  let fiat_amount = 0;

  if (isSell) {
    crypto_amount = rfq?.data?.from_amount ?? rfq?.data?.quantity ?? amount;
    fiat_amount = rfq?.data?.to_amount ?? (unit_price ? Number(crypto_amount) * unit_price : 0);
  } else {
    const isCryptoInput = inputMode === "crypto" || Number(rfq?.data?.to_amount) === Number(amount);
    crypto_amount = Number(
      isCryptoInput
        ? (rfq?.data?.to_amount ?? amount)
        : (rfq?.data?.to_amount ?? rfq?.data?.quantity ?? (unit_price ? Number(amount) / unit_price : 0))
    );
    fiat_amount = Number(
      isCryptoInput
        ? (rfq?.data?.from_amount ?? (unit_price ? Number(amount) * unit_price : 0))
        : (rfq?.data?.from_amount ?? amount ?? 0)
    );
  }

  // const cryptoDisplay = `${formatCryptoAmount(crypto_amount)} ${crypto_currency}`;
  // const fiatDisplay = `${formatFiatDisplay(fiat_amount)} ${fiat_currency}`;
  const cryptoDisplay = `${formatCryptoValue(crypto_amount, cryptoAccount?.decimals)} ${crypto_currency}`;
  const fiatDisplay = `${formatFiatValue(fiat_amount, 2)} ${fiat_currency}`;

  // Source (Left) and Destination (Right) accounts
  const sourceAccount = isSell ? cryptoAccount : fiatAccount;
  const sourceCurrency = isSell ? crypto_currency : fiat_currency;
  const sourceDisplay = isSell ? cryptoDisplay : fiatDisplay;

  const destAccount = isSell ? fiatAccount : cryptoAccount;
  const destCurrency = isSell ? fiat_currency : crypto_currency;
  const destDisplay = isSell ? fiatDisplay : cryptoDisplay;

  const totalValue = isSell
    ? (fees?.net_amount ?? fiat_amount)
    : (fees?.net_amount ?? fiat_amount);

  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="flex flex-col items-center gap-5">
          <div className="flex w-full items-center">
            <div className="flex flex-1 flex-col items-center gap-3">
              <Skeleton className="size-[52px] rounded-full" />
              <Skeleton className="h-8 w-32 rounded-lg" />
            </div>

            <div className="mx-4 flex w-14 shrink-0 justify-center">
              <Skeleton className="size-10 rounded-full" />
            </div>

            <div className="flex flex-1 flex-col items-center gap-3">
              <Skeleton className="size-[52px] rounded-full" />
              <Skeleton className="h-8 w-32 rounded-lg" />
            </div>
          </div>

          <Skeleton className="h-4 w-48 rounded-md" />
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-28 rounded-md" />
            <div className="flex items-center gap-2">
              <Skeleton className="size-6 rounded-full" />
              <Skeleton className="h-4 w-32 rounded-md" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-4 w-20 rounded-md" />
          </div>
          <Skeleton className="h-px w-full rounded-full" />
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-28 rounded-md" />
            <Skeleton className="h-6 w-36 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Exchange Summary (Source -> Arrow -> Destination) */}
      <div className="flex flex-col items-center gap-5">
        <div className="flex w-full items-center">
          {/* From / Source */}
          <div className="flex flex-1 flex-col items-center gap-3">
            <div className="flex size-[52px] items-center justify-center overflow-hidden rounded-full">
              <img
                src={sourceAccount?.logo}
                alt={sourceCurrency}
                className="size-[52px] rounded-full object-cover"
              />
            </div>
            <p className={`text-center font-semibold leading-tight text-text-primary break-words ${getAmountTextClass(sourceDisplay)}`}>
              {sourceDisplay}
            </p>
          </div>

          {/* Transfer Arrow */}
          <div className="text-text-secondary">
            <ChevronRightIcon className="size-[34px]" />
          </div>

          {/* To / Destination */}
          <div className="flex flex-1 flex-col items-center gap-3">
            <div className="flex size-[52px] items-center justify-center overflow-hidden rounded-full">
              <img
                src={destAccount?.logo}
                alt={destCurrency}
                className="size-[52px] rounded-full object-cover"
              />
            </div>
            <p className={`text-center font-semibold leading-tight text-text-primary break-words ${getAmountTextClass(destDisplay)}`}>
              {destDisplay}
            </p>
          </div>
        </div>

        {/* Unit Price Rate */}
        <p className="text-center text-sm font-medium text-text-secondary">
          1 {crypto_currency} ≈ {unit_price} {fiat_currency}
        </p>
      </div>

      {/* Payment / Cost Summary */}
      <div className="flex flex-col gap-3 max-sm:pt-4">
        <div className="flex w-full items-center justify-between">
          <p className="sm:text-sm text-xs font-normal leading-[21px] text-text-secondary">
            Payment Method
          </p>
          <div className="flex items-center gap-1">
            <div className="flex sm:size-6 size-4 shrink-0 items-center justify-center overflow-hidden rounded-full">
              <img
                src={sourceAccount?.logo}
                alt={sourceCurrency}
                className="sm:size-6 size-4 rounded-full object-cover"
              />
            </div>
            <p className="sm:text-sm text-xs sm:font-semibold font-medium leading-[19.5px] text-text-primary">
              {sourceAccount?.title}
            </p>
          </div>
        </div>

        {/* Dynamic Fee Breakdown */}
        {fees?.charges && fees?.charges.map((fee, index) => (
          <SummaryRow
            key={index}
            label={`${fee.label} (${fee.rate}%)`}
            value={`${Number(fee.amount).toFixed(2)} ${fees.currency_short}`}
            valueClassName="text-sm font-medium text-text-primary"
          />
        ))}

        <div className="w-full border-t border-divider" />

        <SummaryRow
          label={isSell ? "Total received" : "Total Spend"}
          value={`${totalValue} ${fiat_currency}`}
          valueClassName="text-base font-semibold text-text-primary"
        />
      </div>
    </>
  );
};

const ConfirmOrderStep = ({
  activeTab = "Buy",
  selectedFiatAccount,
  selectedCryptoAccount,
  amount,
  // modalPhase,
  setModalPhase,
  setRfq,
  inputMode = "fiat",
  spotlightActive = false,
  guideHint = null,
  error,
  setError,
  handleBack,
}) => {
  const company = useSelector((state) => state.ownCompany.company);
  const short_name = process.env.REACT_APP_COMPANY_SHORT_NAME;
  const currency = selectedFiatAccount?.currency_code;
  const symbol = selectedCryptoAccount?.currency_code;

  const dispatch = useDispatch();
  const {
    rfq,
    quoteId,
    timeLeft,
    totalDuration,
    isExpired,
    loading: rfqLoading,
    refetch: refetchRFQ,
  } = useCryptoRFQ({
    cryptoCurrency: selectedCryptoAccount?.currency_code,
    fiatCurrency: selectedFiatAccount?.currency_code,
    side: activeTab.toLowerCase(),
    amount,
    inputMode,
    cryptoAccountId: selectedCryptoAccount?.id,
    fiatAccountId: selectedFiatAccount?.id,
    setError,
    error,
    handleBack,
  });

  const onConfirmOrder = async () => {
    setModalPhase("processing");

    if (!quoteId) return;

    try {
      const isBuy = activeTab.toLowerCase() === "buy";
      const formData = new FormData();
      formData.append("rfq_id", quoteId);
      formData.append("instrument", `${symbol}-${currency}`);
      formData.append("side", activeTab.toLowerCase());

      if (isBuy) {
        if (inputMode === "fiat") {
          formData.append("from_amount", amount);
        } else {
          formData.append("to_amount", amount);
        }
        formData.append("from_account_id", String(selectedFiatAccount.id));
        formData.append("to_account_id", String(selectedCryptoAccount.id));
      } else {
        if (inputMode === "crypto") {
          formData.append("from_amount", amount);
        } else {
          formData.append("to_amount", amount);
        }
        formData.append("from_account_id", String(selectedCryptoAccount.id));
        formData.append("to_account_id", String(selectedFiatAccount.id));
      }

      const order = await dispatch(CreateCryptoOrder(formData));

      if (!order?.success) {
        setError(order?.message ?? "Something went wrong, please try again later.");
        setModalPhase("form");
        handleBack();
        return;
      }

      const summary = getOrderSummary(order.data, selectedCryptoAccount, selectedFiatAccount);
      setRfq(summary);
      setModalPhase("success");
    } catch (err) {
      // console.log("onConfirmOrder ERROR: ", err);
      setModalPhase("form");
      handleBack();
    }
  };

  const isLoading = rfqLoading || !rfq;
  const isConfirmDisabled = isLoading || isExpired || !quoteId;

  return (
    <div
      className={`flex flex-col px-6 pt-4 sm:h-full sm:min-h-0 sm:pt-4 ${
        spotlightActive
          ? "sm:justify-between sm:overflow-y-auto sm:hide-scrollbar"
          : "h-full min-h-0 justify-between overflow-y-auto hide-scrollbar"
      }`}
    >
      <ErrorMessage message={error} onClose={() => setError("")} />

      <h3 className="max-sm:hidden text-base font-semibold leading-7 text-text-primary">
        Confirm Order
      </h3>

      {spotlightActive ? (
        <div className="relative z-40 mt-4 max-sm:shrink-0 max-sm:-mx-6 max-sm:rounded-[12px] max-sm:bg-background-surface sm:mt-0 sm:flex sm:min-h-0 sm:flex-1 sm:flex-col sm:justify-between sm:rounded-none sm:bg-transparent sm:p-0">
          {guideHint && (
            <div className="pointer-events-none absolute inset-x-4 bottom-full z-50 mb-2 sm:hidden">
              <div className="pointer-events-auto mx-auto w-[90%] max-w-[280px]">
                <GuideHint
                  title={guideHint.title}
                  description={guideHint.description}
                  primaryAction={guideHint.primaryAction}
                  secondaryAction={guideHint.secondaryAction}
                  onSkip={guideHint.onSkip}
                  onDone={guideHint.onDone}
                  onDepositCrypto={guideHint.onDepositCrypto}
                />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-6 sm:gap-0 max-sm:max-h-[calc(100dvh-12rem)] max-sm:overflow-y-auto max-sm:hide-scrollbar max-sm:px-6 max-sm:pb-6 max-sm:pt-4 sm:min-h-0 sm:flex-1 sm:justify-between sm:p-0">
            <div className="flex flex-col gap-4">
              <UnifiedConversionSummary
                activeTab={activeTab}
                cryptoAccount={selectedCryptoAccount}
                fiatAccount={selectedFiatAccount}
                amount={amount}
                inputMode={inputMode}
                rfq={rfq}
                loading={isLoading}
              />
              <QuoteExpiryTimer
                timeLeft={timeLeft}
                totalDuration={totalDuration}
                isExpired={isExpired}
                loading={isLoading}
                onRefresh={refetchRFQ}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <UnifiedConversionSummary
            activeTab={activeTab}
            cryptoAccount={selectedCryptoAccount}
            fiatAccount={selectedFiatAccount}
            amount={amount}
            inputMode={inputMode}
            rfq={rfq}
            loading={isLoading}
          />
          <QuoteExpiryTimer
            timeLeft={timeLeft}
            totalDuration={totalDuration}
            isExpired={isExpired}
            loading={isLoading}
            onRefresh={refetchRFQ}
          />
        </div>
      )}

      <div className="flex flex-col sm:pt-8">
        <div className="flex flex-col gap-5">
          <p className="-mt-1 sm:-mt-1 sm:text-xs text-[10px] font-medium leading-[21px] text-text-secondary">
            By clicking &ldquo;Confirm Order&rdquo;, you agree to our{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href={cryptoTremsCondiations}
              className="font-semibold text-[#4ab932] transition-colors hover:underline"
            >
              Buy &amp; Sell Terms
            </a>
            ,{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href={cryptoTremsCondiations}
              className="font-semibold text-[#4ab932] transition-colors hover:underline"
            >
              Terms of Use
            </a>
            , and{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href={cryptoPolicyURL}
              className="font-semibold text-[#4ab932] transition-colors hover:underline"
            >
              Privacy Policy
            </a>
            , and authorize {company && company.short_name ? company.short_name : short_name} to debit your selected payment method.
          </p>

          <div className="flex flex-col gap-3">
            <NewCommonButton
              disabled={isConfirmDisabled}
              onClick={onConfirmOrder}
            >
              Confirm Order
            </NewCommonButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmOrderStep;